import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import {
  Heart,
  Utensils,
  Bus,
  GraduationCap,
  CreditCard,
  Users,
  FileText,
  ChevronRight,
} from 'lucide-react';

type BenefitTab = 'active' | 'usage' | 'card' | 'dependents';

export function EmployeeBenefits() {
  const [activeTab, setActiveTab] = useState<BenefitTab>('active');
  const [selectedBenefit, setSelectedBenefit] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch active benefits
  const { data: benefitsData, isLoading: benefitsLoading } = useQuery({
    queryKey: ['employee', 'benefits', 'active'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/benefits');
      return response.data;
    },
  });

  // Fetch benefit usage
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['employee', 'benefits', 'usage'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/benefits/usage');
      return response.data;
    },
    enabled: activeTab === 'usage',
  });

  // Fetch benefit card
  const { data: cardData, isLoading: cardLoading } = useQuery({
    queryKey: ['employee', 'benefits', 'card'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/benefits/card');
      return response.data;
    },
    enabled: activeTab === 'card',
  });

  // Fetch dependents
  const { data: dependentsData, isLoading: dependentsLoading } = useQuery({
    queryKey: ['employee', 'benefits', 'dependents'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/benefits/dependents');
      return response.data;
    },
    enabled: activeTab === 'dependents',
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const getBenefitIcon = (type: string) => {
    switch (type) {
      case 'HEALTH': return <Heart size={20} className="text-red-400" />;
      case 'MEAL': return <Utensils size={20} className="text-orange-400" />;
      case 'FOOD': return <Utensils size={20} className="text-green-400" />;
      case 'TRANSPORT': return <Bus size={20} className="text-blue-400" />;
      case 'EDUCATION': return <GraduationCap size={20} className="text-purple-400" />;
      default: return <CreditCard size={20} className="text-primary-400" />;
    }
  };

  const getBenefitLabel = (type: string) => {
    switch (type) {
      case 'HEALTH': return 'Plano de Saúde';
      case 'MEAL': return 'Vale Refeição';
      case 'FOOD': return 'Vale Alimentação';
      case 'TRANSPORT': return 'Vale Transporte';
      case 'EDUCATION': return 'Auxílio Educação';
      default: return type;
    }
  };

  const tabs = [
    { id: 'active' as BenefitTab, label: 'Benefícios Ativos', icon: <Heart size={18} /> },
    { id: 'usage' as BenefitTab, label: 'Extrato de Uso', icon: <FileText size={18} /> },
    { id: 'card' as BenefitTab, label: 'Cartão', icon: <CreditCard size={18} /> },
    { id: 'dependents' as BenefitTab, label: 'Dependentes', icon: <Users size={18} /> },
  ];

  const handleBenefitClick = (benefit: any) => {
    setSelectedBenefit(benefit);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Benefícios</h1>
        <p className="text-surface-400">Gerencie seus benefícios corporativos</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active Benefits Tab */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          {benefitsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-surface-700" />
                      <div className="flex-1">
                        <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                        <div className="h-3 w-24 bg-surface-700 rounded" />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : benefitsData?.benefits?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefitsData.benefits.map((benefit: any) => (
                <Card
                  key={benefit.id}
                  className="cursor-pointer hover:border-primary-500/50 transition-colors"
                  onClick={() => handleBenefitClick(benefit)}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-surface-800 flex items-center justify-center">
                        {getBenefitIcon(benefit.type)}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">
                          {getBenefitLabel(benefit.type)}
                        </p>
                        <p className="text-xs text-surface-400">{benefit.provider}</p>
                      </div>
                      <ChevronRight size={20} className="text-surface-500" />
                    </div>

                    {benefit.balance !== undefined && (
                      <div className="mt-4 pt-4 border-t border-surface-700">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-surface-400">Saldo disponível</span>
                          <span className="text-sm font-semibold text-primary-400">
                            {formatCurrency(benefit.balance)}
                          </span>
                        </div>
                        {benefit.monthlyCredit && (
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-surface-400">Crédito mensal</span>
                            <span className="text-xs text-surface-300">
                              {formatCurrency(benefit.monthlyCredit)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <div className="p-8 text-center">
                <Heart size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum benefício ativo</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Usage Tab */}
      {activeTab === 'usage' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Extrato de Uso</h2>

            {usageLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-40 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-4 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : usageData?.transactions?.length > 0 ? (
              <div className="space-y-3">
                {usageData.transactions.map((tx: any) => (
                  <div
                    key={tx.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50"
                  >
                    <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                      {getBenefitIcon(tx.benefitType)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{tx.description}</p>
                      <p className="text-xs text-surface-400">
                        {formatDate(tx.date)} • {getBenefitLabel(tx.benefitType)}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-red-400">
                      -{formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhuma transação encontrada</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Card Tab */}
      {activeTab === 'card' && (
        <div className="space-y-6">
          {cardLoading ? (
            <Card className="animate-pulse">
              <div className="p-6">
                <div className="h-48 bg-surface-700 rounded-xl" />
              </div>
            </Card>
          ) : cardData?.card ? (
            <>
              {/* Virtual Card Display */}
              <div className="relative">
                <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 rounded-2xl p-6 shadow-xl">
                  <div className="flex justify-between items-start mb-8">
                    <div>
                      <p className="text-primary-200 text-xs">Cartão de Benefícios</p>
                      <p className="text-white font-semibold">{cardData.card.provider}</p>
                    </div>
                    <CreditCard size={32} className="text-primary-300" />
                  </div>

                  <p className="text-white text-xl tracking-widest font-mono mb-6">
                    {cardData.card.number || '•••• •••• •••• ••••'}
                  </p>

                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-primary-200 text-xs">Titular</p>
                      <p className="text-white text-sm">{cardData.card.holderName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-primary-200 text-xs">Validade</p>
                      <p className="text-white text-sm">{cardData.card.expiryDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Info */}
              <Card>
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">Informações do Cartão</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-surface-400">Status</span>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        cardData.card.status === 'ACTIVE'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {cardData.card.status === 'ACTIVE' ? 'Ativo' : 'Bloqueado'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Tipo</span>
                      <span className="text-white">{cardData.card.type || 'Virtual'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-surface-400">Bandeira</span>
                      <span className="text-white">{cardData.card.brand || 'Visa'}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <Card>
              <div className="p-8 text-center">
                <CreditCard size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum cartão de benefícios vinculado</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Dependents Tab */}
      {activeTab === 'dependents' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Dependentes</h2>

            {dependentsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-12 h-12 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : dependentsData?.dependents?.length > 0 ? (
              <div className="space-y-3">
                {dependentsData.dependents.map((dependent: any) => (
                  <div
                    key={dependent.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <Users size={24} className="text-primary-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{dependent.name}</p>
                      <p className="text-xs text-surface-400">
                        {dependent.relationship} • {dependent.birthDate ? formatDate(dependent.birthDate) : '-'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-surface-400">Benefícios</p>
                      <p className="text-sm text-white">{dependent.benefitsCount || 0}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum dependente cadastrado</p>
                <p className="text-xs text-surface-500 mt-1">
                  Entre em contato com o RH para adicionar dependentes
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Benefit Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={selectedBenefit ? getBenefitLabel(selectedBenefit.type) : 'Detalhes do Benefício'}
      >
        {selectedBenefit && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-lg bg-surface-800">
              <div className="w-12 h-12 rounded-lg bg-surface-700 flex items-center justify-center">
                {getBenefitIcon(selectedBenefit.type)}
              </div>
              <div>
                <p className="text-sm font-medium text-white">
                  {getBenefitLabel(selectedBenefit.type)}
                </p>
                <p className="text-xs text-surface-400">{selectedBenefit.provider}</p>
              </div>
            </div>

            {selectedBenefit.balance !== undefined && (
              <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/20">
                <p className="text-xs text-primary-300 mb-1">Saldo Disponível</p>
                <p className="text-2xl font-bold text-primary-400">
                  {formatCurrency(selectedBenefit.balance)}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {selectedBenefit.monthlyCredit && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Crédito Mensal</span>
                  <span className="text-white">{formatCurrency(selectedBenefit.monthlyCredit)}</span>
                </div>
              )}
              {selectedBenefit.startDate && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Início</span>
                  <span className="text-white">{formatDate(selectedBenefit.startDate)}</span>
                </div>
              )}
              {selectedBenefit.coverage && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Cobertura</span>
                  <span className="text-white">{selectedBenefit.coverage}</span>
                </div>
              )}
            </div>

            <Button
              variant="secondary"
              className="w-full"
              onClick={() => setShowDetailModal(false)}
            >
              Fechar
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default EmployeeBenefits;
